import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { Opportunity, OpportunityStatus } from '../client-actions.model';

export type OpportunityDialogResult =
  | { action: 'save'; patch: Partial<Opportunity> }
  | { action: 'approve' }
  | { action: 'withdraw' };

const PRIORITY_OPTIONS = [
  { label: 'High', value: 'High' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Low', value: 'Low' },
];

const STATUS_OPTIONS: { label: string; value: OpportunityStatus }[] = [
  { label: 'Potential', value: 'Potential' },
  { label: 'Committed', value: 'Committed' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Withdrawn', value: 'Withdrawn' },
];

@Component({
  selector: 'bai-opportunity-details-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, Button, InputTextModule, SelectModule, TagModule],
  template: `
    <div class="flex flex-col gap-4 p-2">

      <!-- Origin badge -->
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <p-tag
          [value]="opp.sourceType === 'aiAgent' ? 'AI Agent' : 'Human'"
          [severity]="opp.sourceType === 'aiAgent' ? 'info' : 'secondary'"
          styleClass="text-xs"
        />
        <span>Created {{ opp.createdDate }}</span>
        @if (opp.updatedDate !== opp.createdDate) {
          <span>· Updated {{ opp.updatedDate }}</span>
        }
      </div>

      <!-- Editable fields -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="od-desc">
          Description <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="od-desc"
          pInputText
          [ngModel]="form().opportunityDescription"
          (ngModelChange)="patch('opportunityDescription', $event)"
          class="w-full"
          aria-required="true"
        />
        @if (submitted() && !form().opportunityDescription) {
          <span class="text-xs text-red-500" role="alert">Required</span>
        }
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700" for="od-source">Source</label>
        <input
          id="od-source"
          pInputText
          [ngModel]="form().source"
          (ngModelChange)="patch('source', $event)"
          class="w-full"
        />
      </div>

      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="od-priority">Priority</label>
          <p-select
            inputId="od-priority"
            [options]="priorityOptions"
            [ngModel]="form().priority"
            (ngModelChange)="patch('priority', $event)"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700" for="od-status">Status</label>
          <p-select
            inputId="od-status"
            [options]="statusOptions"
            [ngModel]="form().status"
            (ngModelChange)="patch('status', $event)"
            optionLabel="label"
            optionValue="value"
            styleClass="w-full"
          />
        </div>
      </div>

      <!-- Confirm / Withdraw (only when applicable) -->
      @if (showConfirmActions()) {
        <div class="flex gap-2 pt-1 border-t border-gray-100">
          <p-button
            label="Approve"
            icon="pi pi-check"
            severity="success"
            [outlined]="true"
            size="small"
            (onClick)="approve()"
            aria-label="Approve opportunity"
          />
          <p-button
            label="Withdraw"
            icon="pi pi-ban"
            severity="danger"
            [outlined]="true"
            size="small"
            (onClick)="withdraw()"
            aria-label="Withdraw opportunity"
          />
        </div>
      }

      <!-- Footer -->
      <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="cancel()" />
        <p-button label="Save" (onClick)="save()" />
      </div>

    </div>
  `,
})
export class OpportunityDetailsDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config = inject(DynamicDialogConfig);

  readonly opp = this._config.data as Opportunity;
  readonly priorityOptions = PRIORITY_OPTIONS;
  readonly statusOptions = STATUS_OPTIONS;
  readonly submitted = signal(false);

  readonly form = signal({
    opportunityDescription: this.opp.opportunityDescription,
    source: this.opp.source,
    priority: this.opp.priority,
    status: this.opp.status,
  });

  readonly showConfirmActions = computed(
    () =>
      this.opp.sourceType === 'aiAgent' &&
      this.opp.status !== 'Withdrawn' &&
      this.opp.status !== 'Completed'
  );

  patch<K extends keyof ReturnType<typeof this.form>>(key: K, value: ReturnType<typeof this.form>[K]): void {
    this.form.update((f) => ({ ...f, [key]: value }));
  }

  save(): void {
    this.submitted.set(true);
    if (!this.form().opportunityDescription) return;
    const result: OpportunityDialogResult = { action: 'save', patch: { ...this.form() } };
    this._ref.close(result);
  }

  approve(): void {
    this._ref.close({ action: 'approve' } satisfies OpportunityDialogResult);
  }

  withdraw(): void {
    this._ref.close({ action: 'withdraw' } satisfies OpportunityDialogResult);
  }

  cancel(): void {
    this._ref.close(null);
  }

  prioritySeverity(priority: string): 'danger' | 'warn' | 'secondary' {
    if (priority === 'High') return 'danger';
    if (priority === 'Medium') return 'warn';
    return 'secondary';
  }
}
